import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import LeadContext from './Lead.context';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Lead, LEAD_STAGES } from 'models/Lead';
import FourDotLoader from 'components/FourDotLoader';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import {
  NdaStatusUpdateEventData,
  NewFilesEventData,
} from 'services/socketService/socketEventData';
import { LeadNdaProps } from 'hooks/useNdaStatus';
import { BUSINESS_ROUTE, BUYING_PORTFOLIO_ROUTE } from 'data/global';
import useDocusignRedirect from 'hooks/useDocusignRedirect';
import useCanAccessDataRoom from 'hooks/useCanAccessDataRoom';
import useBuyerPulseCheck from 'router/subrouters/Dash/subrouters/hooks/useBuyerPulseCheck';
import PopNotificationWithUndo from 'components/PopNotificationWithUndo';
import { useTranslation } from 'react-i18next';
import { getBuyingPortfolio } from 'utils';
import useBusEvent from 'hooks/useBusEvent';
import bus, { StandardEventTypes } from 'modules/bus';
import useConfirmInterest from '../../hooks/useConfirmInterest';

const ListingProvider: React.FC = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [lead, setLead] = useState<Lead>();
  const [isConfirmingInterestInProgress, setIsConfirmingInterestInProgress] =
    useState(false);

  const { popServerError } = useContext(PopNotificationsContext);

  const { id: enquiryId } = useParams();

  const { isWaitingDocusingWebhook, removeDocusignQueryParams } =
    useDocusignRedirect();

  const isWaitingForWebhook = useRef(isWaitingDocusingWebhook);

  const { pathname } = useLocation();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const canAccessDataRoom = useCanAccessDataRoom()(
    lead?.dealRoom,
    lead?.hasDataRoomAccess,
  );

  const updateLead = useCallback(
    (lead: Partial<Lead>) => setLead((prev) => ({ ...prev, ...lead })),
    [],
  );

  const { onConfirmInterest } = useConfirmInterest(
    lead?._id,
    lead?.listingEnquiry?._id,
    updateLead,
  );

  const rerouteIfWithdrawn = useCallback(() => {
    if (pathname.includes(getBuyingPortfolio(enquiryId))) {
      navigate(BUYING_PORTFOLIO_ROUTE, { replace: true });
    }
  }, [enquiryId, navigate, pathname]);

  const onPulseCheckSuccess = useCallback(
    async (withdrawnInterest: boolean, isConfirmingInterest: boolean) => {
      const shouldConfirm =
        isConfirmingInterest &&
        lead?.buyerStep === LEAD_STAGES.ACCEPT &&
        !lead?.confirmedInterest;

      if (shouldConfirm) {
        setIsConfirmingInterestInProgress(true);
      }

      updateLead({
        pulseCheckActive: false,
        withdrawnInterest,
      });

      if (withdrawnInterest) {
        bus.broadcastEvent(StandardEventTypes.LEAD_WITHDRAW);
      }

      if (shouldConfirm) {
        await onConfirmInterest();
        setIsConfirmingInterestInProgress(false);
      }
    },
    [lead, onConfirmInterest, updateLead],
  );

  const updateUnreadMessagesCount = useCallback(
    (unreadMessagesCount: number) =>
      setLead((prev) => ({ ...prev, unreadMessagesCount })),
    [],
  );

  const updateNdaProps = useCallback(
    (leadNdaProps: LeadNdaProps) =>
      setLead((prev) => ({ ...prev, ...leadNdaProps })),
    [],
  );

  const {
    isUndoAvailable: isUndoAvailableForCheckPulse,
    onUndo: onUndoCheckPulse,
    onSubmit: onSubmitCheckPulse,
  } = useBuyerPulseCheck(lead?._id, onPulseCheckSuccess);

  const value = useMemo(() => {
    const {
      _id: leadId,
      listing,
      listingEnquiry: enquiry,
      buyerStep,
      envelopeId,
      signingStatus,
      signedDate,
      isOfferRejected,
      isOfferAccepted,
      sessionId,
      withdrawnInterest,
      dealRoom,
      confirmedInterest,
      NBIO: nbio,
      manuallySetNDAStatus,
      unreadMessagesCount,
      rejectionReason,
      pulseCheckActive = true,
    } = lead || {};

    return {
      leadId,
      listing,
      enquiry,
      nbio,
      buyerStep,
      signingStatus,
      envelopeId,
      signedDate,
      isOfferRejected,
      isOfferAccepted,
      sessionId,
      withdrawnInterest,
      dealRoom,
      canAccessDataRoom,
      confirmedInterest,
      manuallySetNDAStatus,
      unreadMessagesCount,
      rejectionReason,
      isWaitingForWebhook: isWaitingForWebhook.current,
      pulseCheckActive,
      isUndoAvailableForCheckPulse,
      isConfirmingInterestInProgress,
      updateLead,
      updateNdaProps,
      updateUnreadMessagesCount,
      onUndoCheckPulse,
      onSubmitCheckPulse,
    };
  }, [
    lead,
    canAccessDataRoom,
    isUndoAvailableForCheckPulse,
    isConfirmingInterestInProgress,
    updateLead,
    updateNdaProps,
    updateUnreadMessagesCount,
    onUndoCheckPulse,
    onSubmitCheckPulse,
  ]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const {
          data: { lead },
        } = await api.portfolio.buyer.getBuyingPortfolio(enquiryId);

        updateLead(lead);
      } catch (e) {
        popServerError(e);
        navigate(BUSINESS_ROUTE, { replace: true });
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enquiryId, popServerError, updateLead]);

  const updateEnquiryHandle = useCallback(
    ({
      signingStatus,
      listingEnquiryId,
      envelopeId,
      manuallySetNDAStatus,
    }: NdaStatusUpdateEventData) => {
      if (enquiryId !== listingEnquiryId) return;

      removeDocusignQueryParams();
      isWaitingForWebhook.current = false;

      updateNdaProps({
        signingStatus,
        envelopeId,
        manuallySetNDAStatus,
      });
    },
    [enquiryId, removeDocusignQueryParams, updateNdaProps],
  );

  useSocketEvent(LISTEN_EVENTS.NDA_STATUS_UPDATE, updateEnquiryHandle);

  useBusEvent(StandardEventTypes.LEAD_WITHDRAW, rerouteIfWithdrawn);

  const newFiles = useCallback(
    ({ dataRoomId, dataRoomNodes }: NewFilesEventData) => {
      const currentDataRoomId = lead?.listing?.dataRoomId;

      if (dataRoomId !== currentDataRoomId) return;

      setLead((prev) => ({
        ...prev,
        listing: { ...prev.listing, dataRoomNodes },
      }));
    },
    [lead?.listing?.dataRoomId],
  );

  useSocketEvent(LISTEN_EVENTS.NEW_FILES, newFiles);

  return (
    <LeadContext.Provider value={value}>
      {isLoading ? (
        <FourDotLoader />
      ) : (
        <>
          {children}
          {isUndoAvailableForCheckPulse && (
            <PopNotificationWithUndo
              isUndoAvailable={isUndoAvailableForCheckPulse}
              onUndo={onUndoCheckPulse}
            >
              <p>{t('reactionSentToTheBroker')}</p>
            </PopNotificationWithUndo>
          )}
        </>
      )}
    </LeadContext.Provider>
  );
};

export default ListingProvider;
